import { ApiResponse, MonitoredProduct, MonitorStats } from '../types';

const API_BASE_URL = '/api';

/**
 * 通用API请求函数
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const mergedOptions = { ...defaultOptions, ...options };

  try {
    const response = await fetch(url, mergedOptions);
    const data: ApiResponse<T> = await response.json();

    if (!data.success) {
      throw new Error(data.message || `API请求失败: ${response.status}`);
    }

    return data.data as T;
  } catch (error) {
    console.error(`API请求失败 [${endpoint}]:`, error);
    throw error instanceof Error ? error : new Error('网络请求失败');
  }
}

/**
 * 获取监控产品列表
 */
export async function getMonitoredProducts(): Promise<MonitoredProduct[]> {
  return apiRequest<MonitoredProduct[]>('/monitor/products');
}

/**
 * 添加产品到监控
 */
export async function addProductToMonitor(productId: string, region: string = 'US'): Promise<any> {
  return apiRequest('/monitor/products', {
    method: 'POST',
    body: JSON.stringify({ productId, region }),
  });
}

/**
 * 从监控中移除产品
 */
export async function removeProductFromMonitor(productId: string): Promise<void> {
  return apiRequest(`/monitor/products/${productId}`, {
    method: 'DELETE',
  });
}

/**
 * 获取产品监控统计数据
 */
export async function getMonitorStats(productId: string, days: number = 7): Promise<MonitorStats> {
  return apiRequest<MonitorStats>(`/monitor/products/${productId}/stats?days=${days}`);
}

/**
 * 获取产品详情信息（包含SKU）
 */
export async function getProductDetail(productId: string, region: string = 'US'): Promise<any> {
  return apiRequest(`/monitor/products/${productId}/detail?region=${region}`);
}

/**
 * 获取产品监控记录
 */
export async function getProductMonitorRecords(productId: string, limit: number = 100): Promise<any[]> {
  return apiRequest<any[]>(`/monitor/products/${productId}/records?limit=${limit}`);
}

/**
 * 执行单次产品监控
 */
export async function monitorSingleProduct(productId: string): Promise<any> {
  return apiRequest(`/monitor/products/${productId}/monitor`, {
    method: 'POST',
  });
}

/**
 * 获取监控概览
 */
export async function getMonitorOverview(): Promise<any> {
  return apiRequest('/monitor/overview');
}

/**
 * 获取监控状态
 */
export async function getMonitorStatus(): Promise<{ 
  isMonitoring: boolean; 
  cronPattern?: string;
  useFixedInterval: boolean;
  intervalMinutes: number;
  nextRunTime?: string;
  timeUntilNext?: number;
}> {
  return apiRequest('/monitor/status');
}

/**
 * 启动监控
 */
export async function startMonitoring(cronPattern?: string): Promise<void> {
  return apiRequest('/monitor/start', {
    method: 'POST',
    body: JSON.stringify({ cronPattern }),
  });
}

/**
 * 停止监控
 */
export async function stopMonitoring(): Promise<void> {
  return apiRequest('/monitor/stop', {
    method: 'POST',
  });
}

/**
 * 更新监控间隔
 */
export async function updateMonitorInterval(cronPattern: string): Promise<void> {
  return apiRequest('/monitor/interval', {
    method: 'PUT',
    body: JSON.stringify({ cronPattern }),
  });
}

/**
 * 通用API调用工具
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * 通用的fetch包装器
   */
  private async fetch<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // 检查响应状态
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // 检查响应类型
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('服务器返回了非JSON格式的响应');
      }

      const result: ApiResponse<T> = await response.json();
      return result;
    } catch (error) {
      console.error(`API调用失败 [${options.method || 'GET'}] ${url}:`, error);
      
      return {
        success: false,
        message: (error as Error).message,
        error: (error as Error).message
      };
    }
  }

  /**
   * GET请求
   */
  async get<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.fetch<T>(url, { method: 'GET' });
  }

  /**
   * POST请求
   */
  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.fetch<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT请求
   */
  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.fetch<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.fetch<T>(url, { method: 'DELETE' });
  }
}

/**
 * 默认API客户端实例
 */
export const apiClient = new ApiClient();

/**
 * 监控相关API
 */
export const monitorApi = {
  // 获取监控产品列表
  getProducts: () => apiClient.get('/api/monitor/products'),
  
  // 添加监控产品
  addProduct: (productId: string) => 
    apiClient.post('/api/monitor/products', { productId }),
  
  // 删除监控产品
  removeProduct: (productId: string) => 
    apiClient.delete(`/api/monitor/products/${productId}`),
  
  // 获取监控概览
  getOverview: () => apiClient.get('/api/monitor/overview'),
  
  // 获取产品统计
  getProductStats: (productId: string, days: number = 7) => 
    apiClient.get(`/api/monitor/products/${productId}/stats?days=${days}`),
  
  // 获取产品记录
  getProductRecords: (productId: string, limit: number = 500) => 
    apiClient.get(`/api/monitor/products/${productId}/records?limit=${limit}`),
  
  // 手动执行监控
  executeMonitor: (productId: string) => 
    apiClient.post(`/api/monitor/products/${productId}/monitor`),
  
  // 获取监控状态
  getStatus: () => apiClient.get('/api/monitor/status'),
  
  // 更新监控间隔
  updateInterval: (cronPattern: string) => 
    apiClient.put('/api/monitor/interval', { cronPattern }),
};

/**
 * 显示Toast通知
 */
export const showToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
  const alertClass = {
    success: 'alert-success',
    error: 'alert-error',
    warning: 'alert-warning',
    info: 'alert-info'
  };

  const icons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️'
  };

  const toast = document.createElement('div');
  toast.className = 'toast toast-top toast-end';
  toast.innerHTML = `
    <div class="alert ${alertClass[type]}">
      <span>${icons[type]} ${message}</span>
    </div>
  `;
  
  document.body.appendChild(toast);
  
  // 自动移除
  setTimeout(() => {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast);
    }
  }, type === 'error' ? 5000 : 3000);
}; 